// Browser-direct video PUT to an R2 presigned URL.
//
// Workstream K Slice 1 Stage 4. Single blob, single PUT, XMLHttpRequest for
// upload progress (mirroring r2Upload.ts's putOnce — intentionally NOT imported
// or modified; the video flow is ONE object with a stall timeout, not a batch).
// The Content-Type MUST equal the normalized base MIME presign signed, or R2
// rejects the SigV4 signature.
//
// STALL semantics (not a total-time timeout): a large/slow upload is fine as
// long as bytes keep moving. A timer fires only if NO progress event arrives
// for STALL_TIMEOUT_MS; it is reset on every progress tick and cleared on
// load/error/abort.

const STALL_TIMEOUT_MS = 90_000;

export class VideoUploadHttpError extends Error {
  status: number;
  constructor(status: number, statusText: string) {
    super(`HTTP ${status}: ${statusText || "upload failed"}`);
    this.name = "VideoUploadHttpError";
    this.status = status;
  }
}

export class VideoUploadStallError extends Error {
  constructor() {
    super("upload stalled (no progress)");
    this.name = "VideoUploadStallError";
  }
}

export class VideoUploadAbortError extends Error {
  constructor() {
    super("upload aborted");
    this.name = "VideoUploadAbortError";
  }
}

export type VideoUploadArgs = {
  blob: Blob;
  url: string;
  contentType: string;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
};

export const uploadVideoToR2 = ({
  blob,
  url,
  contentType,
  onProgress,
  signal,
}: VideoUploadArgs): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer = 0;
    let done = false;

    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = 0;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = window.setTimeout(() => {
        if (done) return;
        done = true;
        try { xhr.abort(); } catch { /* noop */ }
        reject(new VideoUploadStallError());
      }, STALL_TIMEOUT_MS);
    };

    const onAbortSignal = () => {
      if (done) return;
      done = true;
      clearStall();
      try { xhr.abort(); } catch { /* noop */ }
      reject(new VideoUploadAbortError());
    };
    if (signal) {
      if (signal.aborted) {
        reject(new VideoUploadAbortError());
        return;
      }
      signal.addEventListener("abort", onAbortSignal, { once: true });
    }

    xhr.upload.onprogress = (e) => {
      armStall(); // reset the stall window on every progress tick
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };

    xhr.onload = () => {
      if (done) return;
      done = true;
      clearStall();
      signal?.removeEventListener("abort", onAbortSignal);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new VideoUploadHttpError(xhr.status, xhr.statusText));
    };

    xhr.onerror = () => {
      if (done) return;
      done = true;
      clearStall();
      signal?.removeEventListener("abort", onAbortSignal);
      reject(new VideoUploadHttpError(0, "network error"));
    };

    xhr.onabort = () => {
      if (done) return;
      done = true;
      clearStall();
      reject(new VideoUploadAbortError());
    };

    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    armStall(); // arm before send in case the first byte never flows
    xhr.send(blob);
  });
